import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'

// ── US States ─────────────────────────────────────────────────────────────────
const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
].map(s => ({ value: s, label: s }))

// ── Steps config ──────────────────────────────────────────────────────────────
const TOTAL_STEPS = 4

// ── Step components ───────────────────────────────────────────────────────────
function StepWelcome({ onNext }) {
  return (
    <div className="flex flex-col items-center text-center gap-6 px-6 py-10 animate-slide-up">
      <div className="text-6xl">👋</div>
      <div>
        <h2 className="text-2xl font-extrabold text-slate-800">Welcome to GoodGuardian</h2>
        <p className="mt-3 text-slate-500 text-sm leading-relaxed">
          We're here to help you protect what matters most — your relationship with your child.
          Let's take a moment to personalize your experience.
        </p>
      </div>
      <div className="disclaimer text-left w-full">
        ⚠️ GoodGuardian provides <strong>legal education</strong>, not legal advice.
        All documents require attorney review before use.
      </div>
      <Button variant="primary" size="lg" fullWidth onClick={onNext}>
        Let's Get Started
      </Button>
    </div>
  )
}

function StepAboutYou({ data, onChange, onNext, onBack }) {
  const valid = data.name.trim().length > 0 && data.email.trim().length > 0
  return (
    <div className="flex flex-col gap-6 px-6 py-8 animate-slide-up">
      <div>
        <h2 className="text-xl font-bold text-slate-800">About you</h2>
        <p className="text-sm text-slate-500 mt-1">We'll use this to personalize your experience.</p>
      </div>

      <div className="flex flex-col gap-4">
        <Input
          label="Your first name"
          type="text"
          value={data.name}
          onChange={e => onChange('name', e.target.value)}
          placeholder="e.g. Alex"
          required
        />
        <Input
          label="Email address"
          type="email"
          value={data.email}
          onChange={e => onChange('email', e.target.value)}
          placeholder="your@email.com"
          required
          hint="We'll send updates about your documents here."
        />
      </div>

      <div className="flex gap-3 mt-auto pt-4">
        <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
        <Button variant="primary" size="md" onClick={onNext} disabled={!valid} className="flex-2 flex-1">
          Continue
        </Button>
      </div>
    </div>
  )
}

function StepYourState({ data, onChange, onNext, onBack }) {
  return (
    <div className="flex flex-col gap-6 px-6 py-8 animate-slide-up">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Your state</h2>
        <p className="text-sm text-slate-500 mt-1">
          Custody laws vary significantly by state. This helps us give you the most accurate guidance.
        </p>
      </div>

      <Input
        label="Which state are you in?"
        type="select"
        value={data.state}
        onChange={e => onChange('state', e.target.value)}
        placeholder="Select your state..."
        options={US_STATES}
        required
      />

      {data.state && (
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 text-sm text-brand-700">
          📍 <strong>{data.state}</strong> — your AI assistant will tailor all guidance to{' '}
          {data.state} family law.
        </div>
      )}

      <div className="flex gap-3 mt-auto pt-4">
        <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
        <Button variant="primary" size="md" onClick={onNext} disabled={!data.state} className="flex-1">
          Continue
        </Button>
      </div>
    </div>
  )
}

const situations = [
  {
    value: 'divorcing',
    emoji: '⚖️',
    label: 'Going through divorce',
    desc: 'Currently in or anticipating divorce proceedings.',
  },
  {
    value: 'custody',
    emoji: '👨‍👧',
    label: 'Custody dispute',
    desc: 'Fighting for or defending custody rights.',
  },
  {
    value: 'cps',
    emoji: '🏛️',
    label: 'CPS involvement',
    desc: 'Navigating a CPS investigation or inquiry.',
  },
  {
    value: 'coparenting',
    emoji: '🤝',
    label: 'Managing co-parenting',
    desc: 'Already divorced; managing an existing arrangement.',
  },
]

function SituationPicker({ selected, onChange }) {
  const toggle = (value) => {
    const current = Array.isArray(selected) ? selected : (selected ? [selected] : [])
    onChange(current.includes(value) ? current.filter(v => v !== value) : [...current, value])
  }
  const isSelected = (value) => {
    const current = Array.isArray(selected) ? selected : (selected ? [selected] : [])
    return current.includes(value)
  }
  return (
    <div className="flex flex-col gap-3">
      {situations.map(s => (
        <button
          key={s.value}
          onClick={() => toggle(s.value)}
          className={[
            'w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all duration-150',
            isSelected(s.value)
              ? 'border-brand-500 bg-brand-50 shadow-card'
              : 'border-slate-200 bg-white hover:border-brand-300',
          ].join(' ')}
        >
          <span className="text-2xl flex-shrink-0">{s.emoji}</span>
          <div>
            <p className="font-semibold text-slate-800 text-sm">{s.label}</p>
            <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
          </div>
          {isSelected(s.value) && (
            <div className="ml-auto flex-shrink-0 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} className="w-3 h-3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      ))}
    </div>
  )
}

function StepSituation({ data, onChange, onNext, onBack }) {
  const selected = Array.isArray(data.situation) ? data.situation : (data.situation ? [data.situation] : [])
  return (
    <div className="flex flex-col gap-6 px-6 py-8 animate-slide-up">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Your situation</h2>
        <p className="text-sm text-slate-500 mt-1">
          Select all that apply — you can change this later in Settings.
        </p>
      </div>

      <SituationPicker selected={data.situation} onChange={v => onChange('situation', v)} />

      <div className="flex gap-3 mt-auto pt-4">
        <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
        <Button variant="primary" size="md" onClick={onNext} disabled={selected.length === 0} className="flex-1">
          Let's Go
        </Button>
      </div>
    </div>
  )
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ step, total }) {
  return (
    <div className="px-6 pt-4">
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={[
              'h-1 flex-1 rounded-full transition-all duration-400',
              i < step ? 'bg-brand-500' : 'bg-slate-200',
            ].join(' ')}
          />
        ))}
      </div>
    </div>
  )
}

// ── Main Onboarding screen ────────────────────────────────────────────────────
export default function Onboarding() {
  const navigate = useNavigate()
  const { setUser, setIsOnboarded } = useApp()

  const [step, setStep] = useState(1)
  const [data, setData] = useState({ name: '', email: '', state: '', situation: [] })

  const handleChange = (field, value) => setData(prev => ({ ...prev, [field]: value }))
  const next = () => setStep(s => Math.min(s + 1, TOTAL_STEPS))
  const back = () => {
    if (step === 1) navigate('/')
    else setStep(s => s - 1)
  }

  const finish = () => {
    setUser(data)
    setIsOnboarded(true)
    navigate('/dashboard')
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pb-2 flex-shrink-0" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
        <button onClick={back} className="text-slate-500 hover:text-slate-600 tap-target flex items-center" aria-label="Go back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="text-xs text-slate-500 font-medium">Step {step} of {TOTAL_STEPS}</span>
        <button onClick={() => { setIsOnboarded(true); navigate('/dashboard') }}
          className="text-xs text-slate-500 hover:text-brand-700 tap-target">
          Skip
        </button>
      </div>

      <ProgressBar step={step} total={TOTAL_STEPS} />

      {/* Step content */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        {step === 1 && <StepWelcome onNext={next} />}
        {step === 2 && <StepAboutYou data={data} onChange={handleChange} onNext={next} onBack={back} />}
        {step === 3 && <StepYourState data={data} onChange={handleChange} onNext={next} onBack={back} />}
        {step === 4 && <StepSituation data={data} onChange={handleChange} onNext={finish} onBack={back} />}
      </div>
    </div>
  )
}
